import type { DataProxy } from 'apollo-cache';
import _ from 'lodash';

import user from 'js/lib/user';

import assignmentInfoQuery from 'bundles/grading/api/AssignmentInfoQuery.graphql';
import submissionDataQuery from 'bundles/grading/api/SubmissionDataQuery.graphql';
import submissionDataQueryV2 from 'bundles/grading/api/SubmissionDataQueryV2.graphql';
import submitterListQuery from 'bundles/grading/api/SubmitterListQuery.graphql';
import type { ApplyGradeOverrideV2Mutation } from 'bundles/grading/api/__generated__/ApplyGradeOverrideMutationV2';
import type { DeleteGradeOverrideV2Mutation } from 'bundles/grading/api/__generated__/DeleteGradeOverrideMutationV2';
import type {
  SubmissionDataV2Query,
  SubmissionDataV2QueryVariables,
} from 'bundles/grading/api/__generated__/SubmissionDataQueryV2';
import type {
  ApplyGradeOverrideMutation,
  AssignmentInfoQuery,
  AssignmentInfoQueryVariables,
  BatchUpdateResponseSgaSubmission,
  BatchUpdateResponseSgaSubmissionManualQuestionElement,
  BatchUpdateResponseSgaSubmissionQuestions,
  DeleteGradeOverrideMutation,
  GradebookEntry,
  GradingStatusSummary,
  ItemType,
  LatestSubmission,
  ManuallyGradedQuestionElement,
  ManuallyGradedQuestionElementScore,
  ManuallyGradedQuestionFormElementNode,
  SubmissionAttemptQueryVariables,
  SubmissionDataQuery,
  SubmissionDataQueryVariables,
  SubmissionStatusEnum,
  SubmitGradesToGradeBookSgaSubmission,
  SubmitterListLatestSubmission,
  SubmitterListQuery,
  SubmitterListQueryVariables,
  UpdateGradeVisibilityMutation,
  UpdateGradeVisibilityMutationVariables,
} from 'bundles/grading/api/types';
import { isGraded, updateGradingStatusSummaryForSubmission } from 'bundles/grading/utils/submissionUtils';
import {
  getLegacySubmitterIdFromNewId,
  getNewSubmitterIdFromLegacyId,
  getScopedSubmitterIdFromRouteSubmitterId,
} from 'bundles/grading/utils/submitterUtils';
import {
  isApplyGradeOverrideResponse,
  isBatchUpdateResponse,
  isDeleteGradeOverrideResponse,
} from 'bundles/grading/utils/typeGuardUtils';

/**
 * Returns score.points value from the updated Question
 * @param { string } questionId
 * @param { BatchUpdateResponseSgaSubmissionQuestions[] | null } updatedQuestions
 * @returns number | null | undefined
 */
export const getUpdatedPoints = (
  questionId: string,
  updatedQuestions: BatchUpdateResponseSgaSubmissionQuestions[] | null
) => {
  const updatedQuestion = updatedQuestions?.find((q) => q.formElementNodeId === questionId);
  return updatedQuestion
    ? (updatedQuestion.formElementNode?.element as BatchUpdateResponseSgaSubmissionManualQuestionElement).score?.points
    : undefined;
};

/**
 * Updates cached SubmissionData with data received from Mutation
 * Returns patched SubmissionData, mostly for test purposes.
 * @param { DataProxy } cache Apollo Cache reference
 * @param { BatchUpdateResponseSgaSubmission | SubmitGradesToGradeBookSgaSubmission } sgaSubmission SGA submission data received from Mutations
 * @param { SubmissionDataQueryVariables } variables variables to read SubmissionData from Apollo Cache
 * @returns patched SubmissionDataQuery | null
 */
export const updateCachedSubmissionData = (
  cache: DataProxy,
  sgaSubmission: BatchUpdateResponseSgaSubmission | SubmitGradesToGradeBookSgaSubmission,
  variables: SubmissionDataQueryVariables,
  gradebookEntry?: GradebookEntry
): SubmissionDataQuery | null => {
  let cachedData = cache.readQuery({
    query: submissionDataQuery,
    variables,
  }) as SubmissionDataQuery;
  cachedData = _.cloneDeep(cachedData);

  // check the cached data whether the latest submission exists or not before continuing
  if (!cachedData?.GroupGrading?.queryGroup?.items[0]?.gradingTasks[0]?.latestSubmission) {
    return null;
  }

  // generate the base patchData to be updated in the cache and then add remaining parts that exist
  // within the sgaSubmission response
  const patchData = {
    GroupGrading: {
      queryGroup: {
        items: [
          {
            gradingTasks: [
              {
                gradebookEntry: {
                  isHidden: gradebookEntry?.isHidden,
                  liveOutcome: gradebookEntry?.liveOutcome,
                  originalOutcome: gradebookEntry?.originalOutcome,
                  overrideOutcome: gradebookEntry?.overrideOutcome,
                } as Partial<GradebookEntry>,
                latestSubmission: {
                  status: sgaSubmission.status,
                  gradingStepActionMetadata: { ...sgaSubmission.gradingStepActionMetadata },
                  lastGradingAction: { ...sgaSubmission.lastGradingAction },
                } as Partial<LatestSubmission>,
              },
            ],
          },
        ],
      },
    },
  };
  if (isBatchUpdateResponse(sgaSubmission) && sgaSubmission.rawScore !== undefined) {
    const responseRawScore = sgaSubmission.rawScore;

    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].latestSubmission.rawScore = responseRawScore;
  }
  if (isBatchUpdateResponse(sgaSubmission)) {
    const patchQuestions = cachedData.GroupGrading.queryGroup.items[0].gradingTasks[0].latestSubmission.questions?.map(
      (cachedQuestion) => {
        // update question's points in cache if updatePoints exist
        const updatedPoints = getUpdatedPoints(cachedQuestion.formElementNodeId, sgaSubmission.questions || null);

        // if there is an updated points available and this is a manually graded question we can apply changes to cache
        if (updatedPoints !== undefined && cachedQuestion.__typename === 'GroupGrading_QuestionManuallyGraded') {
          const newQuestion = { ...cachedQuestion };
          if (
            newQuestion.formElementNode &&
            (
              (newQuestion.formElementNode as ManuallyGradedQuestionFormElementNode)
                .element as ManuallyGradedQuestionElement
            ).score
          ) {
            (
              (
                (newQuestion.formElementNode as ManuallyGradedQuestionFormElementNode)
                  .element as ManuallyGradedQuestionElement
              ).score as ManuallyGradedQuestionElementScore
            ).points = updatedPoints;
          }

          return newQuestion;
        } else {
          return { ...cachedQuestion };
        }
      }
    );
    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].latestSubmission.questions = patchQuestions;
  }

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: submissionDataQuery,
    variables,
    data: result,
  });

  return result;
};

/**
 * Updates cached SubmissionData with data received from Mutation
 * Returns patched SubmissionData, mostly for test purposes.
 * @param { DataProxy } cache Apollo Cache reference
 * @param { BatchUpdateResponseSgaSubmission | SubmitGradesToGradeBookSgaSubmission } sgaSubmission SGA submission data received from Mutations
 * @param { SubmissionDataQueryVariables } variables variables to read SubmissionData from Apollo Cache
 * @returns patched SubmissionDataQuery | null
 */
export const updateCachedSubmissionDataV2 = (
  cache: DataProxy,
  sgaSubmission: BatchUpdateResponseSgaSubmission | SubmitGradesToGradeBookSgaSubmission,
  variables: SubmissionDataQueryVariables,
  courseId: string,
  gradebookEntry?: GradebookEntry
): SubmissionDataV2Query | null => {
  const routeSubmitterId = getLegacySubmitterIdFromNewId(
    variables.gradingTaskFilter?.submitterIds?.[0] || '',
    variables.id
  );
  const scopedSubmitterId = getScopedSubmitterIdFromRouteSubmitterId(routeSubmitterId);

  const variablesV2 = {
    itemId: variables.itemFilter?.itemIds?.[0] || '',
    courseId,
    scopedSubmitterId,
  };

  let cachedData = cache.readQuery({
    query: submissionDataQueryV2,
    variables: variablesV2,
  }) as SubmissionDataV2Query;
  cachedData = _.cloneDeep(cachedData);

  // check the cached data whether the latest submission exists or not before continuing
  if (!cachedData?.GroupGrading?.queryGradingTask?.latestSubmission) {
    return null;
  }

  // generate the base patchData to be updated in the cache and then add remaining parts that exist
  // within the sgaSubmission response
  const patchData = {
    GroupGrading: {
      queryGradingTask: {
        gradebookEntry: {
          isHidden: gradebookEntry?.isHidden,
          liveOutcome: gradebookEntry?.liveOutcome,
          originalOutcome: gradebookEntry?.originalOutcome,
          overrideOutcome: gradebookEntry?.overrideOutcome,
        } as Partial<GradebookEntry>,
        latestSubmission: {
          status: sgaSubmission.status,
          gradingStepActionMetadata: { ...sgaSubmission.gradingStepActionMetadata },
          lastGradingAction: { ...sgaSubmission.lastGradingAction },
        } as Partial<LatestSubmission>,
      },
    },
  };
  if (isBatchUpdateResponse(sgaSubmission) && sgaSubmission.rawScore !== undefined) {
    const responseRawScore = sgaSubmission.rawScore;

    patchData.GroupGrading.queryGradingTask.latestSubmission.rawScore = responseRawScore;
  }
  if (isBatchUpdateResponse(sgaSubmission)) {
    const patchQuestions = cachedData.GroupGrading.queryGradingTask.latestSubmission.questions?.map(
      (cachedQuestion) => {
        // update question's points in cache if updatePoints exist
        const updatedPoints = getUpdatedPoints(cachedQuestion.formElementNodeId, sgaSubmission.questions || null);

        // if there is an updated points available and this is a manually graded question we can apply changes to cache
        if (updatedPoints !== undefined && cachedQuestion.__typename === 'GroupGrading_QuestionManuallyGraded') {
          const newQuestion = { ...cachedQuestion };
          if (
            newQuestion.formElementNode &&
            (
              (newQuestion.formElementNode as ManuallyGradedQuestionFormElementNode)
                .element as ManuallyGradedQuestionElement
            ).score
          ) {
            (
              (
                (newQuestion.formElementNode as ManuallyGradedQuestionFormElementNode)
                  .element as ManuallyGradedQuestionElement
              ).score as ManuallyGradedQuestionElementScore
            ).points = updatedPoints;
          }

          return newQuestion;
        } else {
          return { ...cachedQuestion };
        }
      }
    );
    patchData.GroupGrading.queryGradingTask.latestSubmission.questions = patchQuestions;
  }

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: submissionDataQueryV2,
    variables: variablesV2,
    data: result,
  });

  return result;
};

/**
 *
 * @param { DataProxy } cache Apollo Cache reference
 * @param {AssignmentInfoQueryVariables} variables to pass to `assignmentInfoQuery`
 * @param {SubmissionStatusEnum} cachedStatus status of submission before mutation
 * @param {SubmissionStatusEnum}  newStatus status of submission after mutation
 * @returns patched assignmentInfoQuery
 */
export const updateCachedAssignmentInfo = (
  cache: DataProxy,
  variables: AssignmentInfoQueryVariables,
  cachedStatus: SubmissionStatusEnum,
  newStatus: SubmissionStatusEnum
): AssignmentInfoQuery | null => {
  let cachedData = cache.readQuery({
    query: assignmentInfoQuery,
    variables,
  }) as AssignmentInfoQuery;
  cachedData = _.cloneDeep(cachedData);

  const cachedSummary = cachedData?.GroupGrading?.queryGroup?.items[0]?.gradingStatusSummary;
  if (!cachedSummary) {
    return null;
  }

  const newGradingStatusSummary = updateGradingStatusSummaryForSubmission(
    cachedSummary as GradingStatusSummary,
    cachedStatus,
    newStatus
  );

  const patchData = {
    GroupGrading: {
      queryGroup: {
        items: [
          {
            ...cachedData?.GroupGrading?.queryGroup?.items[0],
            gradingStatusSummary: newGradingStatusSummary,
          },
        ],
      },
    },
  };

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: assignmentInfoQuery,
    variables,
    data: result,
  });

  return result;
};

/**
 * Updates cached SubmitterList's grading status for current submitter
 * Returns patched SubmitterList, mostly for test purposes.
 * @param { DataProxy } cache Apollo Cache reference
 * @param { BatchUpdateResponseSgaSubmission | SubmitGradesToGradeBookSgaSubmission } sgaSubmission SGA submission data received from Mutations used to determine NEW status
 * @param { SubmitterListQueryVariables } variables variables to read SubmitterList from Apollo Cache
 * @param { string } submitterId submitter id or submission expecting to use the 'user~...'/'team~...' format
 * @returns patched SubmitterListQuery | null
 */
export const updateCachedSubmitterGradingStatus = (
  cache: DataProxy,
  sgaSubmission: BatchUpdateResponseSgaSubmission | SubmitGradesToGradeBookSgaSubmission,
  variables: SubmitterListQueryVariables,
  submitterId: string
): SubmitterListQuery | null => {
  let cachedData = cache.readQuery({
    query: submitterListQuery,
    variables,
  }) as SubmitterListQuery;
  cachedData = _.cloneDeep(cachedData);

  const cachedElements = cachedData?.GroupGrading?.queryGroup?.items[0]?.paginatedGradingTasks.elements;
  // check the cached data whether the current submitter given, or the gradingStatusSummary exists or not; if not return null
  if (!cachedElements?.find((task) => task.submitterId === submitterId)) {
    return null;
  }

  // find the cached latestSubmissionSummary for `submitterId`
  const currentLatestSubmissionSummary =
    cachedElements.find((task) => task.submitterId === submitterId)?.latestSubmissionSummary || null;

  // create updated patchData
  const patchData = _.cloneDeep(cachedData);

  // update the status of the submitter itself within the patchData, if the old status in cache is different from the new status from mutation response
  if (
    currentLatestSubmissionSummary &&
    patchData?.GroupGrading?.queryGroup?.items[0]?.paginatedGradingTasks.elements &&
    currentLatestSubmissionSummary.status !== sgaSubmission.status
  ) {
    // find the index of this submitter within the gradingTasks and update the patchData status and isSubmissionGraded with the mutation response value
    const submitterGradingTaskIndex =
      patchData.GroupGrading.queryGroup.items[0].paginatedGradingTasks.elements.findIndex(
        (task) => task.submitterId === submitterId
      );
    if (submitterGradingTaskIndex !== -1) {
      (
        patchData.GroupGrading.queryGroup.items[0].paginatedGradingTasks.elements[submitterGradingTaskIndex]
          .latestSubmissionSummary as SubmitterListLatestSubmission
      ).status = sgaSubmission.status;

      // update patchData's isSubmissionGraded state with the incoming status from response
      patchData.GroupGrading.queryGroup.items[0].paginatedGradingTasks.elements[
        submitterGradingTaskIndex
      ].isSubmissionGraded = isGraded(sgaSubmission.status);
    }
    updateCachedAssignmentInfo(cache, variables, currentLatestSubmissionSummary.status, sgaSubmission.status);
  }

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: submitterListQuery,
    variables,
    data: result,
  });

  return result;
};

/**
 * Updates cached SubmissionData with data received from Mutation
 * Returns patched SubmissionData, mostly for test purposes.
 * @param { DataProxy } cache Apollo Cache reference
 * @param { ApplyGradeOverrideMutation | DeleteGradeOverrideMutation } sgaSubmission SGA submission data received from Mutations
 * @param { SubmissionDataQueryVariables } variables variables to read SubmissionData from Apollo Cache
 * @returns patched SubmissionDataQuery | null
 */
export const updateCachedAssignmentGradeData = (
  cache: DataProxy,
  gradeOverride: ApplyGradeOverrideMutation | DeleteGradeOverrideMutation,
  variables: SubmissionDataQueryVariables
): SubmissionDataQuery | null => {
  let cachedData = cache.readQuery({
    query: submissionDataQuery,
    variables,
  }) as SubmissionDataQuery;
  cachedData = _.cloneDeep(cachedData);

  // check the cached data whether the latest submission exists or not before continuing
  if (!cachedData?.GroupGrading?.queryGroup?.items[0]?.gradingTasks[0]?.latestSubmission) {
    return null;
  }

  // generate the base patchData to be updated in the cache and then add remaining parts that exist
  // within the sgaSubmission response
  const patchData = {
    GroupGrading: {
      queryGroup: {
        items: [
          {
            gradingTasks: [
              {
                gradebookEntry: {} as Partial<GradebookEntry>,
                latestSubmission: {} as Partial<LatestSubmission>,
              },
            ],
          },
        ],
      },
    },
  };

  if (isApplyGradeOverrideResponse(gradeOverride)) {
    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].gradebookEntry = {
      liveOutcome: gradeOverride.GroupGrading_ApplyGradeOverride.liveOutcome,
      originalOutcome: gradeOverride.GroupGrading_ApplyGradeOverride.originalOutcome,
      overrideOutcome: gradeOverride.GroupGrading_ApplyGradeOverride.overrideOutcome,
    };
    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].latestSubmission = {
      lastGradingAction: {
        __typename: 'GroupGrading_GradingAction',
        actionTime: {
          __typename: 'Timestamp',
          seconds: Math.round(Date.now() / 1000).toString(),
        },
        fullName: user.get().fullName,
      },
    };
  }

  if (isDeleteGradeOverrideResponse(gradeOverride)) {
    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].gradebookEntry = {
      liveOutcome: gradeOverride.GroupGrading_DeleteGradeOverride.liveOutcome,
      originalOutcome: gradeOverride.GroupGrading_DeleteGradeOverride.originalOutcome,
      overrideOutcome: gradeOverride.GroupGrading_DeleteGradeOverride.overrideOutcome,
    };
    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].latestSubmission = {
      lastGradingAction: {
        __typename: 'GroupGrading_GradingAction',
        actionTime: {
          __typename: 'Timestamp',
          seconds: Math.round(Date.now() / 1000).toString(),
        },
        fullName: user.get().fullName,
      },
    };
  }

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: submissionDataQuery,
    variables,
    data: result,
  });

  return result;
};

/**
 * Updates cached SubmissionDataV2 with data received from Mutation
 * Returns patched SubmissionDataV2, mostly for test purposes.
 * @param { DataProxy } cache Apollo Cache reference
 * @param { ApplyGradeOverrideV2Mutation | DeleteGradeOverrideV2Mutation } mutationResponse
 * @returns patched SubmissionDataQueryV2 | null
 */
export const updateCachedAssignmentGradeDataV2 = (
  cache: DataProxy,
  mutationResponse: ApplyGradeOverrideV2Mutation | DeleteGradeOverrideV2Mutation
): SubmissionDataV2Query | null => {
  // since the response for Apply and Delete has the same structure
  let responseData;
  if ((mutationResponse as ApplyGradeOverrideV2Mutation).GroupGrading_ApplyGradeOverrideV2) {
    responseData = (mutationResponse as ApplyGradeOverrideV2Mutation).GroupGrading_ApplyGradeOverrideV2;
  } else {
    responseData = (mutationResponse as DeleteGradeOverrideV2Mutation).GroupGrading_DeleteGradeOverrideV2;
  }

  const variables = {
    itemId: responseData.itemId,
    courseId: responseData.courseId,
    scopedSubmitterId: responseData.scopedSubmitterId,
  };

  let cachedData: SubmissionDataV2Query | null = cache.readQuery({
    query: submissionDataQueryV2,
    variables,
  });
  cachedData = _.cloneDeep(cachedData);

  // check the cached data whether the latest submission exists or not before continuing
  if (!cachedData?.GroupGrading?.queryGradingTask?.latestSubmission) {
    return null;
  }

  // generate the base patchData to be updated in the cache and then add remaining parts that exist
  // within the sgaSubmission response
  const patchData = {
    GroupGrading: {
      queryGradingTask: {
        gradebookEntry: {} as Partial<GradebookEntry>,
        latestSubmission: {} as Partial<LatestSubmission>,
      },
    },
  };

  patchData.GroupGrading.queryGradingTask.gradebookEntry = { ...responseData.gradebookEntry };
  patchData.GroupGrading.queryGradingTask.latestSubmission = {
    lastGradingAction: {
      __typename: 'GroupGrading_GradingAction',
      actionTime: {
        __typename: 'Timestamp',
        seconds: Math.round(Date.now() / 1000).toString(),
      },
      fullName: user.get().fullName,
    },
  };

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: submissionDataQueryV2,
    variables,
    data: result,
  });

  return result;
};

/**
 * Updates cached SubmissionData's grade visibility with data
 * received from Mutation
 * Returns patched SubmissionData, mostly for test purposes.
 * @param { DataProxy } cache Apollo Cache reference
 * @param { UpdateGradeVisibilityMutation } UpdateGradeVisibilityMutation response from Mutations
 * @param { SubmissionDataQueryVariables } variables variables to read SubmissionData from Apollo Cache
 * @returns patched SubmissionDataQuery | null
 */
export const updateCachedGradeVisibility = (
  cache: DataProxy,
  updateGradeVisibility: UpdateGradeVisibilityMutation,
  variables: SubmissionDataQueryVariables
): SubmissionDataQuery | null => {
  let cachedData = cache.readQuery({
    query: submissionDataQuery,
    variables,
  }) as SubmissionDataQuery;
  cachedData = _.cloneDeep(cachedData);

  // check the cached data whether the latest submission exists or not before continuing
  if (!cachedData?.GroupGrading?.queryGroup?.items[0]?.gradingTasks[0]?.latestSubmission) {
    return null;
  }

  // generate the base patchData to be updated in the cache and then add remaining parts that exist
  // within the response
  const patchData = {
    GroupGrading: {
      queryGroup: {
        items: [
          {
            gradingTasks: [
              {
                gradebookEntry: {} as Partial<GradebookEntry>,
                latestSubmission: {} as Partial<LatestSubmission>,
              },
            ],
          },
        ],
      },
    },
  };

  // update the hidden and last saved status
  if (updateGradeVisibility?.GroupGrading_UpdateGradeVisibility?.isHidden != null) {
    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].gradebookEntry = {
      isHidden: updateGradeVisibility?.GroupGrading_UpdateGradeVisibility?.isHidden,
    };
    patchData.GroupGrading.queryGroup.items[0].gradingTasks[0].latestSubmission = {
      lastGradingAction: {
        __typename: 'GroupGrading_GradingAction',
        actionTime: {
          __typename: 'Timestamp',
          seconds: Math.round(Date.now() / 1000).toString(),
        },
        fullName: user.get().fullName,
      },
    };
  }

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: submissionDataQuery,
    variables,
    data: result,
  });

  return result;
};

/**
 *
 * @param cache Apollo Cache reference
 * @param updateGradeVisibility response from Mutation
 * @param variables variables to read SubmissionDataV2 from Apollo Cache
 * @returns patched SubmissionDataV2Query | null
 */
export const updateCachedGradeVisibilityV2 = (
  cache: DataProxy,
  updateGradeVisibility: UpdateGradeVisibilityMutation,
  variables: SubmissionDataV2QueryVariables
): SubmissionDataV2Query | null => {
  let cachedData = cache.readQuery({
    query: submissionDataQueryV2,
    variables,
  }) as SubmissionDataV2Query;
  cachedData = _.cloneDeep(cachedData);

  // check the cached data whether the latest submission exists or not before continuing
  if (!cachedData?.GroupGrading?.queryGradingTask?.latestSubmission) {
    return null;
  }

  // generate the base patchData to be updated in the cache and then add remaining parts that exist
  // within the sgaSubmission response
  const patchData = {
    GroupGrading: {
      queryGradingTask: {
        gradebookEntry: {} as Partial<GradebookEntry>,
        latestSubmission: {} as Partial<LatestSubmission>,
      },
    },
  };

  // update the hidden and last saved status
  if (updateGradeVisibility?.GroupGrading_UpdateGradeVisibility?.isHidden !== undefined) {
    patchData.GroupGrading.queryGradingTask.gradebookEntry.isHidden =
      updateGradeVisibility.GroupGrading_UpdateGradeVisibility.isHidden;

    patchData.GroupGrading.queryGradingTask.latestSubmission = {
      lastGradingAction: {
        __typename: 'GroupGrading_GradingAction',
        actionTime: {
          __typename: 'Timestamp',
          seconds: Math.round(Date.now() / 1000).toString(),
        },
        fullName: user.get().fullName,
      },
    };
  }

  // merge the results of cachedData and patchData and write it to the cache
  const result = _.merge(cachedData, patchData);
  cache.writeQuery({
    query: submissionDataQueryV2,
    variables,
    data: result,
  });

  return result;
};

/**
 * Generates the submission data query variables
 *
 * @param { string } groupId
 * @param { string } itemId
 * @param { ItemType } itemType
 * @param { string | undefined } gradingGroupId
 * @returns Omit<SubmitterListQueryVariables, 'limit'|'cursor'>
 */
export const generateSubmitterListQueryVariables = (
  groupId: string,
  itemId: string,
  itemType: ItemType,
  gradingGroupId?: string
): Omit<SubmitterListQueryVariables, 'limit' | 'cursor'> => ({
  id: gradingGroupId || groupId,
  itemFilter: {
    itemIds: [itemId],
    itemTypes: [itemType],
  },
});

/**
 * Generates the submission data query variables
 *
 * @param { string } groupId
 * @param { string } itemId
 * @param { ItemType } itemType
 * @param { string } submitterId
 * @param { string | undefined } gradingGroupId
 * @returns SubmissionDataVariables
 */
export const generateSubmissionDataQueryVariables = (
  groupId: string,
  itemId: string,
  itemType: ItemType,
  submitterId: string,
  gradingGroupId?: string
): SubmissionDataQueryVariables => ({
  id: gradingGroupId || groupId,
  itemFilter: {
    itemIds: [itemId],
    itemTypes: [itemType],
  },
  gradingTaskFilter: {
    submitterIds: [submitterId],
  },
});

/**
 * Generates the UpdateGradeVisibility mutation variables
 *
 * @param { boolean } isHidden
 * @param { string } groupId
 * @param { string } itemId
 * @param { string } legacySubmitterId
 * @param { string | undefined } gradingGroupId
 * @returns UpdateGradeVisibilityMutationVariables
 */
export const generateUpdateGradeVisibilityMutationVariables = (
  isHidden: boolean,
  groupId: string,
  itemId: string,
  legacySubmitterId: string,
  gradingGroupId?: string
): UpdateGradeVisibilityMutationVariables => ({
  input: {
    isHidden,
    submissionReference: {
      groupId: gradingGroupId || groupId,
      itemId,
      submitterId: getNewSubmitterIdFromLegacyId(legacySubmitterId),
    },
  },
});

/**
 * Generate the SubmissionAttemptQueryVariables query variables
 *
 * @param { string } groupId
 * @param { string } itemId
 * @param { string } submitterId
 * @param { string } submissionAttemptId
 * @param { string | undefined } gradingGroupId
 * @returns SubmissionAttemptQueryVariables
 */
export const generateSubmissionAttemptQueryVariables = (
  groupId: string,
  itemId: string,
  submitterId: string,
  submissionAttemptId: string,
  gradingGroupId?: string
): SubmissionAttemptQueryVariables => ({
  id: gradingGroupId || groupId,
  itemId,
  submitterId,
  submissionAttemptId,
});
